import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showError, showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { generatePayroll, getMissedPayrollEmployees, getPayrollTest, toApiMonthYear } from '@/services/payrollService'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import { calendarToApi } from '@/lib/calendarDate'
import { formatCurrency } from '@/lib/format'
import type { DepartmentLookup } from '@/api/types'
import type { MissedPayrollEmployee, PayrollTestItem } from '@/api/types/payroll.types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function getStartEndForMonth(ym: string): { startDate: string; endDate: string } {
  const pad = (n: number) => String(n).padStart(2, '0')
  const d = new Date()
  const year = !ym || ym.length < 7 ? d.getFullYear() : Number(ym.split('-')[0])
  const month = !ym || ym.length < 7 ? d.getMonth() + 1 : Number(ym.split('-')[1])
  const lastDay = new Date(year, month, 0).getDate()
  return {
    startDate: `${year}-${pad(month)}-01`,
    endDate: `${year}-${pad(month)}-${pad(lastDay)}`,
  }
}

function formatNum(n?: number | null): string {
  if (n == null) return '—'
  return formatCurrency(Number(n))
}

export const PayrollAddEmployee: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)
  const navState = (location.state ?? {}) as { monthYear?: string; startDate?: string; endDate?: string }

  const [monthYear, setMonthYear] = useState(navState.monthYear || getDefaultMonthYear())
  const defaultRange = useMemo(() => getStartEndForMonth(monthYear), [monthYear])
  const [startDate, setStartDate] = useState(navState.startDate || defaultRange.startDate)
  const [endDate, setEndDate] = useState(navState.endDate || defaultRange.endDate)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [departmentId, setDepartmentId] = useState(0)
  const [employees, setEmployees] = useState<MissedPayrollEmployee[]>([])
  const [employeeId, setEmployeeId] = useState(0)
  const [preview, setPreview] = useState<PayrollTestItem | null>(null)
  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [saving, setSaving] = useState(false)

  const req = t('salaryDetail.labelRequired')

  useEffect(() => {
    if (!navState.startDate && !navState.endDate) {
      const range = getStartEndForMonth(monthYear)
      setStartDate(range.startDate)
      setEndDate(range.endDate)
    }
  }, [monthYear])

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService
      .getDepartmentLookups()
      .then((list) => {
        if (!cancelled) setDepartments(list ?? [])
      })
      .catch(() => {
        if (!cancelled) setDepartments([])
      })
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    setEmployeeId(0)
    setPreview(null)
    if (!branchId || !departmentId || !monthYear || monthYear.length < 7) {
      setEmployees([])
      return
    }
    let cancelled = false
    setLoadingEmployees(true)
    getMissedPayrollEmployees({
      branchId,
      departmentId,
      monthYear,
      startDate,
      endDate,
    })
      .then((list) => {
        if (!cancelled) setEmployees(list)
      })
      .catch((err: any) => {
        if (!cancelled) {
          setEmployees([])
          toast.error(err?.response?.data?.message || err?.response?.data?.error || t('salaryDetail.addEmpLoadEmployeesFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingEmployees(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, departmentId, monthYear, startDate, endDate, t])

  useEffect(() => {
    setPreview(null)
    if (!branchId || !employeeId) return
    let cancelled = false
    setLoadingPreview(true)
    getPayrollTest({
      branchId,
      monthYear,
      startDate,
      endDate,
      empId: employeeId,
      pageNumber: 1,
      pageSize: 10,
    })
      .then((res) => {
        if (cancelled) return
        const row = (res.data ?? []).find((r) => Number(r.empId) === employeeId) ?? res.data?.[0] ?? null
        setPreview(row)
        if (!row) toast.error(t('salaryDetail.addEmpNoPreview'))
      })
      .catch(async (e: any) => {
        if (cancelled) return
        setPreview(null)
        const data = e?.response?.data
        const msg = typeof data === 'string' ? data : data?.message ?? data?.error ?? ''
        if (e?.response?.status === 400 && String(msg).toLowerCase().includes('post attendance')) {
          await showError(t('salaryDetail.attendanceNotPostedTitle'), t('salaryDetail.attendanceNotPostedMessage'))
        } else {
          toast.error(msg || t('salaryDetail.addEmpNoPreview'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, employeeId, monthYear, startDate, endDate, t])

  const departmentOptions = useMemo(
    () =>
      departments
        .filter((d) => d.id)
        .map((d) => ({
          value: String(d.id),
          label: d.lookupNameEn || d.lookupNameAr || String(d.id),
        })),
    [departments],
  )

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: String(e.id),
        label:
          e.employeeName ||
          [e.empFirstNameEn, e.empFatherNameEn, e.empFamilyNameEn].filter(Boolean).join(' ').trim() ||
          e.empCode ||
          t('salaryDetail.employeeFallback', { id: e.id }),
      })),
    [employees, t],
  )

  const detailRows = preview
    ? [
        { label: t('salaryDetail.testColEmpCode'), value: preview.empCode ?? '—' },
        { label: t('salaryDetail.testColEmployee'), value: preview.employeeName ?? '—' },
        { label: t('salaryDetail.testColDepartment'), value: preview.department ?? '—' },
        { label: t('salaryDetail.testColProject'), value: preview.project ?? '—' },
        { label: t('salaryDetail.testColBasic'), value: formatNum(preview.basicSalary) },
        { label: t('salaryDetail.testColHousing'), value: formatNum(preview.housingAllowance) },
        { label: t('salaryDetail.testColTransport'), value: formatNum(preview.transportationAllowance) },
        { label: t('salaryDetail.testColMeal'), value: formatNum(preview.mealAllowance) },
        { label: t('salaryDetail.testColOther'), value: formatNum(preview.otherAllowance) },
        { label: t('salaryDetail.testColTotalAllowance'), value: formatNum(preview.totalAllowance) },
        { label: t('salaryDetail.testColGross'), value: formatNum(preview.grossSalary) },
        { label: t('salaryDetail.testColBonus'), value: formatNum(preview.bonus) },
        { label: t('salaryDetail.testColIncentive'), value: formatNum(preview.incentive) },
        { label: t('salaryDetail.testColOtAmount'), value: formatNum(preview.otAmount) },
        { label: t('salaryDetail.testColGosi'), value: formatNum(preview.gosi) },
        { label: t('salaryDetail.testColLoanDeduction'), value: formatNum(preview.loanDeduction) },
        { label: t('salaryDetail.testColAbsentDeduction'), value: formatNum(preview.absentDeduction) },
        { label: t('salaryDetail.testColLateLeaveDays'), value: String(preview.lateLeaveDays ?? 0) },
        { label: t('salaryDetail.testColLateDeduction'), value: formatNum(preview.lateDeduction) },
        { label: t('salaryDetail.testColEarlyOutDeduction'), value: formatNum(preview.earlyOutDeduction) },
        { label: t('salaryDetail.testColTotalDeduction'), value: formatNum(preview.totalDeduction) },
        { label: t('salaryDetail.testColNetSalary'), value: formatNum(preview.netSalary) },
      ]
    : []

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('salaryDetail.selectBranch'))
      return
    }
    if (!departmentId) {
      toast.error(t('salaryDetail.addEmpDepartmentRequired'))
      return
    }
    if (!employeeId) {
      toast.error(t('salaryDetail.addEmpEmployeeRequired'))
      return
    }
    if (!preview) {
      toast.error(t('salaryDetail.addEmpNoPreview'))
      return
    }
    setSaving(true)
    try {
      await generatePayroll({
        monthYear: toApiMonthYear(monthYear),
        startDate: calendarToApi(startDate),
        endDate: calendarToApi(endDate),
        branchId,
        empId: employeeId,
      })
      let remainingAmount: number | null = null
      try {
        const remaining = await payrollPaymentService.isAnyPaymentRemaining(branchId, departmentId, monthYear)
        remainingAmount = Number(remaining?.remainingAmount ?? 0)
      } catch {
        remainingAmount = Number(preview.netSalary ?? 0)
      }
      await showSuccess(
        t('salaryDetail.addEmpSavedTitle'),
        remainingAmount != null && remainingAmount > 0
          ? t('salaryDetail.addEmpSavedWithPayment', { amount: formatNum(remainingAmount) })
          : t('salaryDetail.addEmpSavedMessage'),
      )
      navigate('/payroll')
    } catch (e: any) {
      const data = e?.response?.data
      const msg = typeof data === 'string' ? data : data?.message ?? data?.error ?? ''
      if (e?.response?.status === 400 && String(msg).toLowerCase().includes('post attendance')) {
        await showError(t('salaryDetail.attendanceNotPostedTitle'), t('salaryDetail.attendanceNotPostedMessage'))
      } else {
        toast.error(msg || t('salaryDetail.addEmpSaveFailed'))
      }
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('salaryDetail.addEmpPageTitle')} description={t('salaryDetail.addEmpPageDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">{t('salaryDetail.selectBranch')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('salaryDetail.addEmpPageTitle')} description={t('salaryDetail.addEmpPageDescription')}>
        <Button variant="outline" onClick={() => navigate('/payroll')}>
          {t('salaryDetail.addEmpBack')}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('salaryDetail.addEmpCardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>
                {req} {t('salaryDetail.labelMonthYear')}
              </Label>
              <Input type="month" value={monthYear} onChange={(e) => setMonthYear(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                {req} {t('salaryDetail.labelStartDate')}
              </Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                {req} {t('salaryDetail.labelEndDate')}
              </Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                {req} {t('salaryDetail.addEmpDepartment')}
              </Label>
              <SearchableSelect
                options={departmentOptions}
                value={departmentId ? String(departmentId) : ''}
                onValueChange={(v) => setDepartmentId(v ? Number(v) : 0)}
                placeholder={t('salaryDetail.addEmpSelectDepartment')}
                searchPlaceholder={t('salaryDetail.searchPlaceholder')}
                emptyMessage={t('salaryDetail.addEmpEmptyDepartment')}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {req} {t('salaryDetail.addEmpEmployee')}
              </Label>
              <SearchableSelect
                options={employeeOptions}
                value={employeeId ? String(employeeId) : ''}
                onValueChange={(v) => setEmployeeId(v ? Number(v) : 0)}
                placeholder={
                  loadingEmployees
                    ? t('salaryDetail.loading')
                    : departmentId
                      ? t('salaryDetail.addEmpSelectEmployee')
                      : t('salaryDetail.addEmpSelectDepartmentFirst')
                }
                searchPlaceholder={t('salaryDetail.searchPlaceholder')}
                emptyMessage={t('salaryDetail.addEmpEmptyEmployee')}
                disabled={!departmentId || loadingEmployees}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {employeeId > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('salaryDetail.addEmpDetailsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPreview ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : preview ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {detailRows.map((row) => (
                  <div key={row.label} className="rounded-md border px-3 py-2">
                    <div className="text-xs text-muted-foreground">{row.label}</div>
                    <div className="text-sm font-medium">{row.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-8 text-center text-muted-foreground">{t('salaryDetail.addEmpNoPreview')}</p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/payroll')}>
          {t('salaryDetail.addEmpCancel')}
        </Button>
        <Button onClick={() => void handleSave()} disabled={saving || !preview}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {t('salaryDetail.addEmpSave')}
        </Button>
      </div>
    </div>
  )
}
